import {
  doc,
  getDoc,
  setDoc,
  increment,
  Timestamp,
  collection,
  query,
  orderBy,
  getDocs,
  updateDoc,
  where,
  limit,
  addDoc,
} from 'firebase/firestore';
import { db } from './firestore';

// Daily + Weekly usage tracking
export interface DailyUsage {
  autoAddCount: number;
  downloadCount: number;
  exportCount: number;
  compressCount: number;
  feedbackCount: number;
  inboxAICount: number;
  date: string; // YYYY-MM-DD format
  weekKey: string; // YYYY-WW format for weekly tracking
  weeklyAutoAddCount: number;
  weeklyCompressCount: number;
}

const getTodayKey = () => {
  const now = new Date();
  return now.toISOString().split('T')[0]; // YYYY-MM-DD
};

const getWeekKey = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 1);
  const diff = now.getTime() - start.getTime();
  const oneWeek = 604800000;
  const weekNumber = Math.ceil(diff / oneWeek);
  return `${now.getFullYear()}-W${String(weekNumber).padStart(2, '0')}`;
};

const getNextResetTime = (type: 'daily' | 'weekly'): Date => {
  const now = new Date();
  if (type === 'daily') {
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    return tomorrow;
  }
  // Weekly: next Monday
  const dayOfWeek = now.getDay();
  const daysUntilMonday = dayOfWeek === 0 ? 1 : 8 - dayOfWeek;
  const nextMonday = new Date(now);
  nextMonday.setDate(nextMonday.getDate() + daysUntilMonday);
  nextMonday.setHours(0, 0, 0, 0);
  return nextMonday;
};

export const getTimeUntilReset = (type: 'daily' | 'weekly'): string => {
  const now = new Date();
  const reset = getNextResetTime(type);
  const diffMs = reset.getTime() - now.getTime();
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

  if (hours > 24) {
    const days = Math.floor(hours / 24);
    return `${days}d ${hours % 24}h`;
  }
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const getDailyUsageRef = (userId: string) =>
  doc(db, 'users', userId, 'usage', 'daily');

export const getDailyUsage = async (userId: string): Promise<DailyUsage> => {
  const ref = getDailyUsageRef(userId);
  const docSnap = await getDoc(ref);

  const today = getTodayKey();
  const currentWeek = getWeekKey();

  if (docSnap.exists()) {
    const data = docSnap.data() as DailyUsage;
    const isNewDay = data.date !== today;
    const isNewWeek = data.weekKey !== currentWeek;

    return {
      autoAddCount: isNewDay ? 0 : (data.autoAddCount || 0),
      downloadCount: isNewDay ? 0 : (data.downloadCount || 0),
      exportCount: isNewDay ? 0 : (data.exportCount || 0),
      compressCount: isNewDay ? 0 : (data.compressCount || 0),
      feedbackCount: isNewDay ? 0 : (data.feedbackCount || 0),
      inboxAICount: isNewDay ? 0 : (data.inboxAICount || 0),
      date: today,
      weekKey: currentWeek,
      weeklyAutoAddCount: isNewWeek ? 0 : (data.weeklyAutoAddCount || 0),
      weeklyCompressCount: isNewWeek ? 0 : (data.weeklyCompressCount || 0),
    };
  }

  return {
    autoAddCount: 0,
    downloadCount: 0,
    exportCount: 0,
    compressCount: 0,
    feedbackCount: 0,
    inboxAICount: 0,
    date: today,
    weekKey: currentWeek,
    weeklyAutoAddCount: 0,
    weeklyCompressCount: 0,
  };
};

export type UsageField = 'autoAddCount' | 'downloadCount' | 'exportCount' | 'compressCount' | 'feedbackCount' | 'inboxAICount' | 'weeklyAutoAddCount' | 'weeklyCompressCount';

export const incrementUsage = async (
  userId: string,
  field: UsageField
): Promise<void> => {
  const ref = getDailyUsageRef(userId);
  const today = getTodayKey();
  const currentWeek = getWeekKey();
  const current = await getDailyUsage(userId);

  await setDoc(ref, {
    ...current,
    [field]: (current[field] || 0) + 1,
    date: today,
    weekKey: currentWeek,
    updatedAt: Timestamp.now(),
  }, { merge: true });
};

export type ActionType = 'autoAdd' | 'download' | 'export' | 'compress' | 'feedback' | 'inboxAI';

export const canPerformAction = async (
  userId: string,
  action: ActionType,
  isPremium: boolean,
  limits: {
    maxAutoAddPerDay: number;
    maxAutoAddPerWeek?: number;
    maxDownloadsPerDay: number;
    maxExportsPerDay: number;
    maxCompressPerWeek?: number;
    maxFeedbackPerDay?: number;
    maxInboxAIPerDay?: number;
  }
): Promise<{ allowed: boolean; remaining: number; message?: string; resetType?: 'daily' | 'weekly' }> => {
  const usage = await getDailyUsage(userId);

  switch (action) {
    case 'autoAdd': {
      if (isPremium) {
        const remaining = limits.maxAutoAddPerDay - usage.autoAddCount;
        if (remaining <= 0) {
          return {
            allowed: false,
            remaining: 0,
            message: 'Daily auto-add limit reached. Resets tomorrow.',
            resetType: 'daily',
          };
        }
        return { allowed: true, remaining, resetType: 'daily' };
      }
      // Free: weekly limit
      const weeklyLimit = limits.maxAutoAddPerWeek || 1;
      const remaining = weeklyLimit - usage.weeklyAutoAddCount;
      if (remaining <= 0) {
        return {
          allowed: false,
          remaining: 0,
          message: 'Weekly auto-add limit reached. Upgrade to Premium for more!',
          resetType: 'weekly',
        };
      }
      return { allowed: true, remaining, resetType: 'weekly' };
    }
    case 'download': {
      if (isPremium) {
        return { allowed: true, remaining: Infinity, resetType: 'daily' };
      }
      const remaining = limits.maxDownloadsPerDay - usage.downloadCount;
      if (remaining <= 0) {
        return {
          allowed: false,
          remaining: 0,
          message: 'Daily download limit reached. Upgrade to Premium for unlimited downloads!',
          resetType: 'daily',
        };
      }
      return { allowed: true, remaining, resetType: 'daily' };
    }
    case 'export': {
      if (isPremium) {
        return { allowed: true, remaining: Infinity, resetType: 'daily' };
      }
      const remaining = limits.maxExportsPerDay - usage.exportCount;
      if (remaining <= 0) {
        return {
          allowed: false,
          remaining: 0,
          message: 'Daily export limit reached. Upgrade to Premium for unlimited exports!',
          resetType: 'daily',
        };
      }
      return { allowed: true, remaining, resetType: 'daily' };
    }
    case 'compress': {
      if (isPremium) {
        return { allowed: true, remaining: Infinity, resetType: 'weekly' };
      }
      const weeklyLimit = limits.maxCompressPerWeek || 1;
      const remaining = weeklyLimit - usage.weeklyCompressCount;
      if (remaining <= 0) {
        return {
          allowed: false,
          remaining: 0,
          message: 'Weekly compression limit reached. Upgrade to Premium for unlimited compression!',
          resetType: 'weekly',
        };
      }
      return { allowed: true, remaining, resetType: 'weekly' };
    }
    case 'feedback': {
      const dailyLimit = isPremium ? (limits.maxFeedbackPerDay || 4) : (limits.maxFeedbackPerDay || 1);
      const remaining = dailyLimit - usage.feedbackCount;
      if (remaining <= 0) {
        return {
          allowed: false,
          remaining: 0,
          message: isPremium
            ? 'Daily feedback limit reached. Resets tomorrow.'
            : 'Daily feedback limit reached. Upgrade to Premium for more!',
          resetType: 'daily',
        };
      }
      return { allowed: true, remaining, resetType: 'daily' };
    }
    case 'inboxAI': {
      if (!isPremium) {
        return {
          allowed: false,
          remaining: 0,
          message: 'AI email analysis is a Premium feature.',
          resetType: 'daily',
        };
      }
      const dailyLimit = limits.maxInboxAIPerDay || 4;
      const remaining = dailyLimit - usage.inboxAICount;
      if (remaining <= 0) {
        return {
          allowed: false,
          remaining: 0,
          message: 'Daily AI analysis limit reached. Resets tomorrow.',
          resetType: 'daily',
        };
      }
      return { allowed: true, remaining, resetType: 'daily' };
    }
  }
};

// Testimonial submission for free premium
export interface TestimonialSubmission {
  id?: string;
  userId: string;
  userName: string;
  userEmail: string;
  userPhoto?: string | null;
  rating: number; // 1-5 stars
  review: string;
  photoUrl?: string | null; // Testimonial photo
  photoSize?: number | null;
  videoUrl?: string | null;
  videoSize?: number | null;
  status: 'pending' | 'approved' | 'rejected';
  hidden?: boolean; // Hide from public display
  premiumGranted: boolean;
  premiumGrantedAt?: string | null;
  rejectionReason?: string | null;
  createdAt: string;
  reviewedAt?: string | null;
  reviewedBy?: string | null;
}

export const submitTestimonial = async (
  userId: string,
  userName: string,
  userEmail: string,
  userPhoto: string | undefined,
  rating: number,
  review: string,
  photoUrl?: string,
  photoSize?: number
): Promise<{ success: boolean; error?: string }> => {
  try {
    const testimonialRef = doc(db, 'testimonials', userId);
    const existingDoc = await getDoc(testimonialRef);

    // Check if user already submitted
    if (existingDoc.exists()) {
      const existing = existingDoc.data() as TestimonialSubmission;
      if (existing.status === 'approved' && existing.premiumGranted) {
        return { success: false, error: 'You have already claimed your free Premium reward.' };
      }
      if (existing.status === 'pending') {
        return { success: false, error: 'Your testimonial is already pending review.' };
      }
    }

    // Normalize undefined values to null for Firestore compatibility
    const submission: TestimonialSubmission = {
      userId,
      userName,
      userEmail,
      userPhoto: userPhoto ?? null,
      rating,
      review,
      photoUrl: photoUrl ?? null,
      photoSize: photoSize ?? null,
      videoUrl: null,
      videoSize: null,
      status: 'pending',
      premiumGranted: false,
      createdAt: new Date().toISOString(),
    };

    await setDoc(testimonialRef, submission);
    return { success: true };
  } catch (error) {
    console.error('Error submitting testimonial:', error);
    return { success: false, error: 'Failed to submit testimonial' };
  }
};

export const getTestimonialStatus = async (userId: string): Promise<TestimonialSubmission | null> => {
  try {
    const testimonialRef = doc(db, 'testimonials', userId);
    const docSnap = await getDoc(testimonialRef);

    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as TestimonialSubmission;
    }
    return null;
  } catch (error) {
    console.error('Error getting testimonial status:', error);
    return null;
  }
};

// Admin/Moderator: Get all testimonials for review
export const getAllTestimonials = async (): Promise<TestimonialSubmission[]> => {
  try {
    const testimonialsRef = collection(db, 'testimonials');
    const q = query(testimonialsRef, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as TestimonialSubmission[];
  } catch (error) {
    console.error('Error fetching all testimonials:', error);
    return [];
  }
};

// Admin/Moderator: Approve a testimonial and grant premium
export const approveTestimonial = async (
  testimonialId: string,
  reviewerEmail: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const testimonialRef = doc(db, 'testimonials', testimonialId);
    const docSnap = await getDoc(testimonialRef);

    if (!docSnap.exists()) {
      return { success: false, error: 'Testimonial not found' };
    }

    const data = docSnap.data() as TestimonialSubmission;

    // Grant 14-day premium
    const userId = data.userId;
    const now = new Date();
    const expiresAt = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000); // 14 days

    // Check if user already has premium subscription
    const subscriptionRef = doc(db, `users/${userId}/account/subscription`);
    const subSnap = await getDoc(subscriptionRef);

    let finalExpiry = expiresAt.toISOString();
    if (subSnap.exists()) {
      const existingSub = subSnap.data();
      const existingExpiry = new Date(existingSub.expiresAt);
      if (existingExpiry > now) {
        // Extend existing subscription by 14 days
        finalExpiry = new Date(existingExpiry.getTime() + 14 * 24 * 60 * 60 * 1000).toISOString();
      }
    }

    // Update subscription
    await setDoc(subscriptionRef, {
      plan: 'premium',
      interval: 'testimonial_reward',
      purchasedAt: now.toISOString(),
      expiresAt: finalExpiry,
      paymentId: `testimonial_${testimonialId}`,
      orderId: `testimonial_reward_${testimonialId}`,
      updatedAt: now.toISOString(),
    });

    // Update testimonial status
    await updateDoc(testimonialRef, {
      status: 'approved',
      premiumGranted: true,
      premiumGrantedAt: now.toISOString(),
      reviewedAt: now.toISOString(),
      reviewedBy: reviewerEmail,
    });

    console.log(`Testimonial ${testimonialId} approved by ${reviewerEmail}`);
    return { success: true };
  } catch (error) {
    console.error('Error approving testimonial:', error);
    return { success: false, error: 'Failed to approve testimonial' };
  }
};

// Admin/Moderator: Reject a testimonial
export const rejectTestimonial = async (
  testimonialId: string,
  reviewerEmail: string,
  reason: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const testimonialRef = doc(db, 'testimonials', testimonialId);
    const now = new Date();

    await updateDoc(testimonialRef, {
      status: 'rejected',
      rejectionReason: reason,
      reviewedAt: now.toISOString(),
      reviewedBy: reviewerEmail,
    });

    console.log(`Testimonial ${testimonialId} rejected by ${reviewerEmail}`);
    return { success: true };
  } catch (error) {
    console.error('Error rejecting testimonial:', error);
    return { success: false, error: 'Failed to reject testimonial' };
  }
};

// Admin/Moderator: Toggle hide status of a testimonial
export const toggleHideTestimonial = async (
  testimonialId: string,
  hide: boolean
): Promise<{ success: boolean; error?: string }> => {
  try {
    const testimonialRef = doc(db, 'testimonials', testimonialId);

    await updateDoc(testimonialRef, {
      hidden: hide,
    });

    console.log(`Testimonial ${testimonialId} ${hide ? 'hidden' : 'unhidden'}`);
    return { success: true };
  } catch (error) {
    console.error('Error toggling hide testimonial:', error);
    return { success: false, error: 'Failed to toggle hide testimonial' };
  }
};

// Feedback system
export interface FeedbackReply {
  id: string;
  message: string;
  repliedBy: string; // moderator/admin uid
  repliedByName: string;
  repliedAt: string;
}

export interface FeedbackSubmission {
  id?: string;
  userId: string;
  userName: string;
  userEmail: string;
  isPremium: boolean;
  tag: 'report' | 'issue' | 'doubt' | 'suggestion';
  message: string;
  status: 'open' | 'in_progress' | 'resolved';
  replies?: FeedbackReply[];
  hasUnreadReply?: boolean; // Flag for user notification
  resolvedBy?: string | null;
  resolvedByName?: string | null;
  resolvedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export const submitFeedback = async (
  userId: string,
  userName: string,
  userEmail: string,
  isPremium: boolean,
  tag: FeedbackSubmission['tag'],
  message: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const feedbackRef = collection(db, 'feedback');
    await addDoc(feedbackRef, {
      userId,
      userName,
      userEmail,
      isPremium,
      tag,
      message,
      status: 'open',
      replies: [],
      hasUnreadReply: false,
      resolvedBy: null,
      resolvedByName: null,
      resolvedAt: null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    } as Omit<FeedbackSubmission, 'id'>);
    return { success: true };
  } catch (error) {
    console.error('Error submitting feedback:', error);
    return { success: false, error: 'Failed to submit feedback' };
  }
};

export const getAllFeedback = async (): Promise<FeedbackSubmission[]> => {
  try {
    const feedbackRef = collection(db, 'feedback');
    const q = query(feedbackRef, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({
      id: d.id,
      ...d.data(),
    })) as FeedbackSubmission[];
  } catch (error) {
    console.error('Error fetching feedback:', error);
    return [];
  }
};

export const updateFeedback = async (
  feedbackId: string,
  updates: Partial<FeedbackSubmission>
): Promise<{ success: boolean; error?: string }> => {
  try {
    const feedbackDocRef = doc(db, 'feedback', feedbackId);

    // Remove undefined values to prevent Firestore errors
    const cleanUpdates = Object.entries(updates).reduce((acc, [key, value]) => {
      if (value !== undefined) {
        acc[key] = value;
      }
      return acc;
    }, {} as Record<string, any>);

    await updateDoc(feedbackDocRef, {
      ...cleanUpdates,
      updatedAt: new Date().toISOString(),
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating feedback:', error);
    return { success: false, error: 'Failed to update feedback' };
  }
};

// Add reply to feedback
export const addFeedbackReply = async (
  feedbackId: string,
  reply: string,
  repliedBy: string,
  repliedByName: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const feedbackDocRef = doc(db, 'feedback', feedbackId);
    const feedbackSnap = await getDoc(feedbackDocRef);

    if (!feedbackSnap.exists()) {
      return { success: false, error: 'Feedback not found' };
    }

    const currentReplies = feedbackSnap.data().replies || [];
    const newReply: FeedbackReply = {
      id: `reply_${Date.now()}`,
      message: reply,
      repliedBy,
      repliedByName,
      repliedAt: new Date().toISOString(),
    };

    await updateDoc(feedbackDocRef, {
      replies: [...currentReplies, newReply],
      hasUnreadReply: true, // Set notification flag
      updatedAt: new Date().toISOString(),
    });

    return { success: true };
  } catch (error) {
    console.error('Error adding reply:', error);
    return { success: false, error: 'Failed to add reply' };
  }
};

// Mark feedback as read (clear notification)
export const markFeedbackAsRead = async (
  feedbackId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const feedbackDocRef = doc(db, 'feedback', feedbackId);
    await updateDoc(feedbackDocRef, {
      hasUnreadReply: false,
      updatedAt: new Date().toISOString(),
    });
    return { success: true };
  } catch (error) {
    console.error('Error marking feedback as read:', error);
    return { success: false, error: 'Failed to mark as read' };
  }
};

// Get user's feedback
export const getUserFeedback = async (userId: string): Promise<FeedbackSubmission[]> => {
  try {
    const feedbackRef = collection(db, 'feedback');
    const q = query(feedbackRef, where('userId', '==', userId), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({
      id: d.id,
      ...d.data(),
    })) as FeedbackSubmission[];
  } catch (error) {
    console.error('Error fetching user feedback:', error);
    return [];
  }
};

// Check if user has unread feedback replies
export const hasUnreadFeedbackReplies = async (userId: string): Promise<boolean> => {
  try {
    const feedbackRef = collection(db, 'feedback');
    const q = query(
      feedbackRef,
      where('userId', '==', userId),
      where('hasUnreadReply', '==', true),
      limit(1)
    );
    const snapshot = await getDocs(q);
    return !snapshot.empty;
  } catch (error) {
    console.error('Error checking unread replies:', error);
    return false;
  }
};
